/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {ComponentRuntimeWrapper, ControllerComponent, ControllerWrapper} from '@adobe/exc-app/components';
import {Events} from '../Events';
import {messenger} from '../Messenger';
import Shell from '../api/ShellApi';
import User from '../user';

let controllerWrapperInstance: ControllerWrapper;
let runtimeInstance: ComponentRuntimeWrapper;

let loadingController: Promise<ControllerWrapper>;
let loadingRuntime: Promise<ComponentRuntimeWrapper>;

function syncShell(): Promise<void> {
  return new Promise((resolve): void => {
    const onMsg = (raw: MessageEvent): void => {
      if (raw.data.type === Events.CAAS_READY) {
        messenger.off('message', onMsg);
        resolve();
      }
    };
    messenger.on('message', onMsg);
    messenger.send(Events.CAAS_LOAD);
  });
}

export function loadRuntime(): Promise<ComponentRuntimeWrapper> {
  if (runtimeInstance) {
    return Promise.resolve(runtimeInstance);
  }

  if (loadingRuntime) {
    return loadingRuntime;
  }

  return loadingRuntime = import('@exc/components').then(module => {
    const {ComponentRuntime, DISPLAY_TYPES, MESSAGE_TYPES, REGISTERED_COMPONENTS} = module;
    return runtimeInstance = {
      constants: {
        DISPLAY_TYPES,
        MESSAGE_TYPES,
        REGISTERED_COMPONENTS
      },
      runtime: new ComponentRuntime()
    };
  });
}

export function getRuntimeValues(): Promise<any> {
  return Promise.all([
    new Shell(messenger).get('environment'),
    User().get('locale'),
    User().get('imsOrg'),
    User().get('imsToken')
  ]).then(([env, local, org, token]) => {
    return {env, local, org, token};
  });
}

export function loadController(): Promise<any> {
  if (controllerWrapperInstance) {
    return Promise.resolve(controllerWrapperInstance);
  }

  if (loadingController) {
    return loadingController;
  }

  return loadingController = Promise.all([
    import('@exc/components'),
    syncShell()
  ]).then(([CAAS]): ControllerWrapper => {
    const {Controller, DISPLAY_TYPES, MESSAGE_TYPES, REGISTERED_COMPONENTS} = CAAS;
    const controllerInstance = new Controller();
    return controllerWrapperInstance = {
      constants: {
        DISPLAY_TYPES,
        MESSAGE_TYPES,
        REGISTERED_COMPONENTS
      },
      controller: {
        createComponent: function(type: string, config: any, id: number): Promise<ControllerComponent> {
          return getRuntimeValues().then(runtimeConfig => {
            config = Object.assign({}, runtimeConfig, config);
            return controllerInstance.createComponent(type, config, id);
          });
        },
        destroyComponent: controllerInstance.destroyComponent
      }
    };
  });
}

// Defined and exported separately because Controller is loaded in solution
// and runtime by a component. Runtime can't depend on shell configuration
// async loading because of that, it'll just hang.
export default {
  loadController,
  loadRuntime
};
